import template from './mx-shell-nav-item.html';
import type { RecursionCompiler } from '../../../Shell/PandoraComponents/core/utils/mx-recursion';
import type { IAngularEvent, IAttributes, IAugmentedJQuery, IComponentController, IDirective, IScope, ITimeoutService, IWindowService } from 'angular';
import { EventCodes } from '../../../App/model/event-codes';
import type { DeviceService } from '../../../App/services/device-service';
import type { NavigationManager } from '../../mx-shell-nav-manager';
import type { MxNavigationService } from '../../mx-navigation-service';
import type { EventService } from '../../../App/services/event-service';
import type { RoutingService } from '../../../App/services/mx-routing-service';
import type { NavigationItem } from '../../model/navigation-item';
import { mx } from '../../../App/globals';
import { NavigationItemImplementation } from '../../../Shell/PandoraSolutionBuilderAgent/model/enumerations';

mxShellNavItemDirective.$inject = ['mx.components.Recursion'];
export function mxShellNavItemDirective(recursion: RecursionCompiler) {
	const directive: IDirective = {
		bindToController: {
			item: '<mxShellNavItem',
			mxShellNavItemOrigin: '<mxShellNavItemOrigin',
			horizontalNavMode: '<',
		},
		compile(element) {
			return recursion.compile(element);
		},
		controller: MxShellNavItemCtrl,
		controllerAs: 'vm',
		restrict: 'A',
		scope: {},
		template: template,
	};

	return directive;
}

/**
 * @ngdoc directive
 * @name mx.shell:mxShellNavItem
 * @module mx.shell
 * @restrict 'A'
 * @scope {}
 * @requires <a href="https://material.angularjs.org/latest/api/service/$mdSidenav" target="_blank">$mdSidenav</a>
 * @requires {@ref mx.components.Recursion mx.components.Recursion}
 * @requires {@ref mx.shell.EventService mx.shell.EventService}
 * @requires {@ref mx.shell.RoutingService mx.shell.RoutingService}
 * @requires {@ref mx.shell.NavigationManager mx.shell.NavigationManager}
 * @requires {@ref mx.internationalization mx.internationalization}
 * @requires $mdSidenav
 * @description
 * Presentation of a sidebar navigation item
 *
 * @param {mx.shell.NavigationItem} item= - {@ref mx.shell.NavigationItem Navigation Item model}
 * @usage <li mx-shell-nav-item="item"></li>
 */

export class MxShellNavItemCtrl implements IComponentController {
	private tryShowMoreItemsDebounced: () => void;
	private item?: NavigationItem;
	private htmlClass?: string;
	private itemTitleClass?: string;
	private mxShellNavItemOrigin?: NavigationItem;
	private __limitTo?: number;

	static readonly $inject = [
		'$scope',
		'$mdSidenav',
		'mx.app.DeviceService',
		'$timeout',
		'$element',
		'$attrs',
		'$window',
		'mx.shell.NavigationManager',
		'mx.shell.NavigationService',
		'mx.shell.EventService',
		'mx.shell.RoutingService',
	];

	constructor(
		private readonly $scope: IScope,
		private readonly $mdSidenav: angular.material.ISidenavService,
		private readonly deviceService: DeviceService,
		private readonly $timeout: ITimeoutService,
		private readonly $element: IAugmentedJQuery,
		private readonly $attrs: IAttributes,
		private readonly $window: IWindowService,
		private readonly navigationManager: NavigationManager,
		private readonly navigationService: MxNavigationService,
		private readonly eventService: EventService,
		private readonly routingService: RoutingService
	) {
		this.eventService.subscribeTo(EventCodes.NavToggled, () => this.onNavBarToggled(), this.$scope);

		this.tryShowMoreItemsDebounced = $.debounce(100, () => this.tryShowMoreItems());

		this.$scope.$watchCollection('vm.item.children', (children) => {
			if (Array.isArray(children)) {
				this.tryInitShowMore();
			}
		});

		this.$scope.$watch('vm.item.selected', () => {
			this.tryInitShowMore();
		});
	}

	$onInit() {
		if (this.item) {
			// set level
			if (this.item.level === undefined) {
				this.item.level = this.item.parent && this.item.parent.level !== undefined ? this.item.parent.level + 1 : 0;
			}

			this.htmlClass = `mx-nav-item--${(this.item ? this.item.name : '').replaceAll('.', '--')}`;
			this.item.htmlClass = this.htmlClass;
			// TODO: why do we need `vm.itemTitleClass` if we have `vm.htmlClass` ???
			// If it's need to use `title` for some reason - why don't just search elements by it's selector and content?
			this.itemTitleClass = `mx-nav-item--auto-${this.item.name.toLowerCase().replace('&', '-').split(' ').join('')}`;
		}
	}

	handleArrowClick($event: IAngularEvent, item: NavigationItem) {
		$event.preventDefault();
		$event.stopPropagation?.();

		if (item.unwrapped) {
			this.$timeout(() => {
				item.unwrapped = false;
			});
		} else {
			this.navigationService.loadChildren(item);
			item.unwrapped = true;
		}
	}

	onNavBarToggled() {
		if (this.item?.selected) {
			this.item.selected = false;
		}
	}

	handleItemClick($event: MouseEvent, item: NavigationItem) {
		if ($event) {
			$event.preventDefault();
			$event.stopPropagation?.();

			$($event.target!).trigger('blur');
		}

		let itemToBeOpen = item;
		while (!itemToBeOpen.route && itemToBeOpen.children?.length) {
			const firstChild = itemToBeOpen.children[0];

			if (firstChild.implementationType === NavigationItemImplementation.ExternalUrl) {
				break;
			} else {
				itemToBeOpen = firstChild;
			}
		}
		this.eventService.dispatchEvent(EventCodes.BeforeNavigationItemClickHandler, item);
		const handleItemClickPromise = this.routingService.isCurrent(itemToBeOpen.route ?? '', itemToBeOpen.parameters)
			? this.routingService.reload()
			: this.navigationService.setCurrentNavigationItem(itemToBeOpen);
		this.eventService.dispatchEvent(EventCodes.TrackTelemetry, { eventName: `Navigation clicked`, sender: item.id, senderTitle: item.technicalName });

		handleItemClickPromise
			.then(() => {
				this.eventService.dispatchEvent(EventCodes.NavigationItemClicked, { navigationItem: itemToBeOpen, $event });

				if (this.deviceService.screenMobile() && itemToBeOpen.route) {
					this.$mdSidenav('mx-shell-left-sidebar').close();
				}

				setTimeout(() => {
					const focusTarget = $('.md-tab.md-active .mx-shell-nav-tabs-level__link, #DWP-main-content')[0];
					if (focusTarget) {
						focusTarget.focus();
					} else {
						$(document.activeElement!).blur();
					}
				}, 300);
			})
			.catch(mx.logCatch);
	}

	tryInitShowMore() {
		this.__limitTo = 50;
		if (this.__limitTo < (this.item?.children?.length ?? 0) && this.item?.selected) {
			this.tryShowMoreItemsDebounced();
			this.$window.addEventListener('scroll', () => this.tryShowMoreItemsDebounced(), true);
		}
	}

	tryShowMoreItems() {
		const showMoreBtn = this.$element.find('.mx-shell-nav-item--show-more');
		if (showMoreBtn.length > 0 && (showMoreBtn.offset()?.top ?? 0) < ($(this.$window).height() ?? 0)) {
			showMoreBtn.click();
		} else if (showMoreBtn.length === 0) {
			this.$window.removeEventListener('scroll', () => this.tryShowMoreItemsDebounced(), true);
		}
	}
}
